import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { useCart } from '@/hooks/useCart';
import { Product } from '@/types/product.types';
import { productService } from '@/services/product.service';
import {
  homeBoxesService,
  homeComingBoxesService,
  HomeBox,
  HomeBoxType,
} from '@/services/home-boxes.service';
import { formatPrice } from '@/utils/formatters';
import '@/styles/boutique.css';

type HomeBoxProductsProps = {
  boxType?: HomeBoxType;
};

export const HomeBoxProducts = ({ boxType = 'collection' }: HomeBoxProductsProps) => {
  const { slug } = useParams();
  const boxesService = boxType === 'coming_soon' ? homeComingBoxesService : homeBoxesService;
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [loading, setLoading] = useState(true);
  const [box, setBox] = useState<HomeBox | null>(null);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    const loadData = async () => {
      if (!slug) return;
      try {
        const [boxData, allProducts] = await Promise.all([
          boxesService.getPublicBySlug(slug),
          productService.getAll(),
        ]);
        setBox(boxData);
        const selectedIds = new Set(boxData.product_ids || []);
        setProducts(
          allProducts.filter(
            (p) => selectedIds.has(p.id) && p.status === 'published' && p.stock_status === 'in-stock'
          )
        );
      } catch (error) {
        console.error('Erreur chargement box produits:', error);
        setBox(null);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug, boxType]);

  const sortedProducts = useMemo(() => {
    if (!box) return products;
    const order = box.product_ids || [];
    return [...products].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  }, [products, box]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="boutique-page">
        <div className="boutique-container">
          <div className="boutique-header">
            <h1 className="boutique-title">
              <span className="boutique-title-prefix">{box?.title || 'Sélection'}</span>
            </h1>
            <p className="boutique-subtitle">{box?.description || 'Produits associés à cette campagne'}</p>
          </div>

          {loading ? (
            <div className="boutique-state"><p>Chargement...</p></div>
          ) : sortedProducts.length === 0 ? (
            <div className="boutique-state"><p className="boutique-state-message">Aucun produit disponible pour cette box.</p></div>
          ) : (
            <div className="boutique-grid">
              {sortedProducts.map((product) => {
                const finalPrice = product.reduced_price || product.price;
                const hasDiscount = product.reduced_price && product.reduced_price < product.price;
                return (
                  <div key={product.id} className="boutique-product-card" onClick={() => navigate(`/produit-detail?id=${product.id}`)}>
                    <div className="boutique-product-image-wrapper">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="boutique-product-image" />
                      ) : (
                        <div className="boutique-product-image-empty"><span>Image non disponible</span></div>
                      )}
                      {hasDiscount && (
                        <div className="boutique-discount-badge">
                          -{Math.round(((product.price - finalPrice) / product.price) * 100)}%
                        </div>
                      )}
                    </div>
                    <div className="boutique-product-content">
                      <h3 className="boutique-product-title">{product.name}</h3>
                      <div className="boutique-price-row">
                        <span className="boutique-price-current">{formatPrice(finalPrice)}</span>
                        {hasDiscount && <span className="boutique-price-old">{formatPrice(product.price)}</span>}
                      </div>
                      <button
                        className="btn-add-to-cart-boutique w-full px-4 py-2 bg-[#8C1843] text-white border-none rounded-lg text-sm font-bold cursor-pointer"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          await addToCart(product.id, 1);
                        }}
                      >
                        Ajouter au panier
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};
